import { useState, useEffect, useCallback } from "react";
import { Plus, GripVertical } from "lucide-react";
import api from "../lib/api";
import { PageHeader, Loading, Stamp } from "../components/kit";
import { Button } from "../components/ui/button";
import { OpportunityModal } from "../components/actions";
import { PIPELINE_STAGES, STAGE_COLORS, money } from "../lib/constants";
import { toast } from "sonner";

export default function Pipeline() {
  const [opps, setOpps] = useState(null);
  const [modal, setModal] = useState(false);
  const [edit, setEdit] = useState(null);
  const [dragId, setDragId] = useState(null);

  const load = useCallback(async () => { const { data } = await api.get("/opportunities?limit=500"); setOpps(data.items); }, []);
  useEffect(() => { load(); }, [load]);

  const drop = async (stage) => {
    if (!dragId) return;
    const opp = opps.find((o) => o.id === dragId);
    setDragId(null);
    if (!opp || opp.stage === stage) return;
    setOpps((prev) => prev.map((o) => (o.id === dragId ? { ...o, stage } : o)));
    try { await api.put(`/opportunities/${dragId}`, { stage }); toast.success(`Moved to ${stage}`); } catch { load(); }
  };

  if (!opps) return <Loading label="Loading pipeline" />;
  const byStage = (s) => opps.filter((o) => (o.stage || "New Lead") === s);
  const totalValue = opps.reduce((a, o) => a + Number(o.estimated_value || 0), 0);

  return (
    <div className="animate-fade-up">
      <PageHeader title="Sales Pipeline" subtitle={`${opps.length} opportunities · ${money(totalValue)} total pipeline value`} testid="pipeline-header"
        actions={<Button className="bg-navy-800 hover:bg-navy-700 text-white" onClick={() => { setEdit(null); setModal(true); }} data-testid="new-opp-btn"><Plus className="w-4 h-4 mr-1.5" />New Opportunity</Button>} />

      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
        {PIPELINE_STAGES.map((stage) => {
          const items = byStage(stage);
          const val = items.reduce((a, o) => a + Number(o.estimated_value || 0), 0);
          return (
            <div key={stage} onDragOver={(e) => e.preventDefault()} onDrop={() => drop(stage)} data-testid={`kanban-col-${stage.toLowerCase().replace(/ /g, "-")}`}
              className="w-72 shrink-0 bg-slate-100/70 rounded-xl border border-slate-200">
              <div className="px-4 py-3 flex items-center justify-between border-b border-slate-200">
                <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: STAGE_COLORS[stage] || "#1E3A8A" }} /><p className="font-display font-semibold text-navy-800 text-sm">{stage}</p></div>
                <span className="text-xs font-semibold text-slate-400 bg-white rounded-full px-2 py-0.5">{items.length}</span>
              </div>
              <div className="p-3 space-y-2 min-h-[120px]">
                {val > 0 && <p className="text-[11px] text-slate-400 font-mono">{money(val)}</p>}
                {items.map((o) => (
                  <div key={o.id} draggable onDragStart={() => setDragId(o.id)} onClick={() => { setEdit(o); setModal(true); }} data-testid={`opp-card-${o.id}`}
                    className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm hover:shadow-md hover:border-gold/40 cursor-grab active:cursor-grabbing transition-[box-shadow,border-color]">
                    <div className="flex items-start gap-2"><GripVertical className="w-3.5 h-3.5 text-slate-300 mt-0.5 shrink-0" /><p className="text-sm font-medium text-navy-800 leading-tight">{o.name}</p></div>
                    <div className="flex items-center justify-between mt-2 pl-5">
                      <span className="text-xs font-semibold text-emerald-600">{money(o.estimated_value)}</span>
                      <span className="text-[10px] uppercase tracking-wide text-slate-400">{o.role}</span>
                    </div>
                    {o.probability > 0 && <div className="mt-2 pl-5"><div className="h-1 rounded-full bg-slate-100"><div className="h-full rounded-full bg-gold" style={{ width: `${o.probability}%` }} /></div></div>}
                    <div className="mt-2 pl-5"><Stamp prefix={o.updated_by_name && o.updated_at !== o.created_at ? "Updated by" : "By"} by={(o.updated_at !== o.created_at && o.updated_by_name) || o.created_by_name || "System"} at={o.updated_at || o.created_at} testid={`opp-stamp-${o.id}`} /></div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <OpportunityModal open={modal} onClose={() => { setModal(false); load(); }} opportunity={edit} onSaved={load} />
    </div>
  );
}
